import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  KnowledgeArticleDto,
  KnowledgeArticleUpsertRequestDto,
} from 'src/api/FiretrolApiClient';
import { knowledgeArticleApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';

export type { KnowledgeArticleUpsertRequestDto };

const cacheArticle = (qc: QueryClient) => (article: KnowledgeArticleDto) =>
  qc.setQueryData(queryKeys.knowledgeArticles.detail(article.id), article);

export function useCreateKnowledgeArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: KnowledgeArticleUpsertRequestDto) =>
      knowledgeArticleApiClient.create(request),
    meta: { successMessage: 'Article created' },
    onSuccess: cacheArticle(qc),
    onSettled: () => invalidateFor('knowledgeArticle.create', qc),
  });
}

export function useUpdateKnowledgeArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: number;
      request: KnowledgeArticleUpsertRequestDto;
    }) => knowledgeArticleApiClient.update(id, request),
    meta: { successMessage: 'Article updated' },
    onSuccess: cacheArticle(qc),
    onSettled: () => invalidateFor('knowledgeArticle.update', qc),
  });
}

export function useArchiveKnowledgeArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => knowledgeArticleApiClient.archive(id),
    meta: { successMessage: 'Article archived' },
    onSuccess: cacheArticle(qc),
    onSettled: () => invalidateFor('knowledgeArticle.archive', qc),
  });
}

export function useRestoreKnowledgeArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => knowledgeArticleApiClient.restore(id),
    meta: { successMessage: 'Article restored' },
    onSuccess: cacheArticle(qc),
    onSettled: () => invalidateFor('knowledgeArticle.restore', qc),
  });
}
